/**
 * PipelineStepList — horizontal step tabs above the editor area.
 *
 * Renders steps in three layout zones:
 *   Input Vizs | Transform Strip | Output Vizs
 *
 * Composed from extracted sub-components:
 *   - SortableStepChip  (./SortableStepChip) — individual step chip
 *   - VizZone / TransformStrip (./DragLayer) — drag-sortable zone containers
 *   - useZoneClassifier  (@/hooks/useZoneClassifier) — zone classification hook
 *
 * @see FR-VIZ-1b, FR-VIZ-1c
 */

import { type DragEndEvent,PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { Braces, Bug,Plus, Save } from 'lucide-react';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { SharePipelinePopover } from '@/components/pipeline/SharePipelinePopover';
import { useZoneClassifier } from '@/hooks/useZoneClassifier';
import { cn } from '@/lib/utils';
import { useAppStore } from '@/store/useAppStore';
import { useEditorStore } from '@/store/useEditorStore';

import { TransformStrip,VizZone } from './DragLayer';

export interface PipelineStepListProps {
  selectedStepId: string | null;
  onSelectStep: (stepId: string) => void;
  onOpenSettings?: (stepId: string) => void;
  onOpenPipelineSettings?: () => void;
  onSavePipeline?: () => void;
  onAddStep?: () => void;
  debugActive?: boolean;
  onToggleDebug?: () => void;
}

export const PipelineStepList: React.FC<PipelineStepListProps> = ({
  selectedStepId,
  onSelectStep,
  onOpenSettings,
  onOpenPipelineSettings,
  onSavePipeline,
  onAddStep,
  debugActive,
  onToggleDebug,
}) => {
  const { t } = useTranslation('pipeline');
  const { reorderPipeline, removeTool, toggleToolEnabled } = useAppStore();
  const pipeline = useEditorStore((s) => s.pipeline);
  const { zones, transformStepNumbers } = useZoneClassifier(pipeline);

  const handleOpenSettings = useCallback((stepId: string) => {
    onOpenSettings?.(stepId);
  }, [onOpenSettings]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = pipeline.findIndex((s) => s.id === active.id);
      const newIndex = pipeline.findIndex((s) => s.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      reorderPipeline(oldIndex, newIndex);
    },
    [pipeline, reorderPipeline]
  );

  // Shared props for all zone components
  const zoneProps = {
    selectedStepId,
    onSelectStep,
    onOpenSettings: handleOpenSettings,
    removeTool,
    toggleToolEnabled,
    sensors,
    onDragEnd: handleDragEnd,
  };

  if (pipeline.length < 1) {
    return (
      <div className="h-[34px] flex items-center gap-1 px-3 border-b border-border bg-background shrink-0 overflow-hidden">
        <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium shrink-0 mr-1">{t('titleLabel')}</span>
        <button
          className="h-5 px-2 text-[10px] rounded-[7px] border border-dashed border-zinc-500 text-muted-foreground hover:border-violet-400 hover:text-violet-400 transition-all duration-150 shrink-0"
          title={t('steps.add')}
          aria-label={t('steps.add')}
          onClick={() => onAddStep?.()}
        >
          <Plus className="h-3 w-3" />
        </button>
        <span className="text-[10px] text-muted-foreground/40 italic px-1 shrink-0 select-none">
          {t('steps.addTool')}
        </span>
      </div>
    );
  }

  const hasHeadVizs = zones.headVizs.length > 0;
  const hasTailVizs = zones.tailVizs.length > 0;

  return (
    <div className="h-[34px] flex items-center gap-1 px-3 border-b border-border bg-background shrink-0 overflow-hidden">
      <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium shrink-0 mr-1">{t('titleLabel')}</span>

      {/* ── Input Viz Box ── */}
      <VizZone label="In" hints={zones.headVizs} zoneType="head-viz" {...zoneProps} />

      {hasHeadVizs && <span className="text-violet-400/25 text-[10px] shrink-0">→</span>}

      {/* ── Transform Strip ── */}
      <TransformStrip hints={zones.transforms} stepNumbers={transformStepNumbers} {...zoneProps} />

      {hasTailVizs && <span className="text-violet-400/25 text-[10px] shrink-0">→</span>}

      {/* ── Output Viz Box ── */}
      <VizZone label="Out" hints={zones.tailVizs} zoneType="tail-viz" {...zoneProps} />

      {/* Add step button — left-aligned after pipeline chips */}
      <button
        className="h-5 px-2 text-[10px] rounded-[7px] border border-dashed border-zinc-500 text-muted-foreground hover:border-violet-400 hover:text-violet-400 transition-all duration-150 shrink-0"
        title={t('steps.add')}
        onClick={() => onAddStep?.()}
      >
        <Plus className="h-3 w-3" />
      </button>

      {/* Right-side actions */}
      <div className="flex items-center gap-1.5 ml-auto shrink-0">
        <button
          className={cn(
            'h-5 px-2 text-[10px] rounded-[7px] border transition-all duration-150',
            debugActive
              ? 'border-violet-400 text-violet-400 bg-violet-400/[0.08]'
              : 'border-border text-muted-foreground hover:border-zinc-500 hover:text-foreground'
          )}
          title={debugActive ? t('debug.close') : t('debug.open')}
          onClick={() => onToggleDebug?.()}
        >
          <Bug className="h-3 w-3" />
        </button>
        <SharePipelinePopover variant="compact" />
        <button
          className="h-5 px-2 text-[10px] rounded-[7px] border border-border text-muted-foreground hover:border-zinc-500 hover:text-foreground transition-all duration-150 flex items-center gap-1"
          title={t('variables.button')}
          onClick={() => onOpenPipelineSettings?.()}
        >
          <Braces className="h-3 w-3" />
        </button>
        <button
          className="h-5 px-2 text-[10px] rounded-[7px] border border-border text-muted-foreground hover:border-zinc-500 hover:text-foreground transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed"
          title={pipeline.length === 0 ? t('steps.addFirst') : t('save.button')}
          onClick={() => onSavePipeline?.()}
          disabled={pipeline.length === 0}
        >
          <Save className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
};
